import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * UsernameStep — Username selection with @ prefix and availability check.
 *
 * @param {Object} props
 * @param {string} props.username - Current username value
 * @param {function} props.onUsernameChange - Called with new username value
 * @param {function} props.onContinue - Called when username is confirmed
 * @param {function} props.onBack - Called to go back
 * @param {function} props.checkUsernameAvailability - Async check function
 * @returns {JSX.Element}
 */
function UsernameStep({ username, onUsernameChange, onContinue, onBack, checkUsernameAvailability }) {
  const [error, setError] = useState(null);
  const [available, setAvailable] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const availabilityCacheRef = useRef(new Map());

  // Debounced availability check
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!username || username.length < 3) {
      setChecking(false);
      setAvailable(null);
      setError(null);
      return;
    }

    if (username.length > 20) {
      setChecking(false);
      setError('Username must be 20 characters or fewer');
      setAvailable(null);
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setChecking(false);
      setError('Only letters, numbers, and underscores allowed');
      setAvailable(null);
      return;
    }

    const cachedAvailability = availabilityCacheRef.current.get(username);
    if (typeof cachedAvailability === 'boolean') {
      setChecking(false);
      setAvailable(cachedAvailability);
      setError(cachedAvailability ? null : 'This username is already taken');
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setChecking(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const isAvailable = await checkUsernameAvailability(username);
        if (requestIdRef.current !== requestId) {
          return;
        }
        availabilityCacheRef.current.set(username, isAvailable);
        setAvailable(isAvailable);
        setError(isAvailable ? null : 'This username is already taken');
      } catch (availabilityError) {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setAvailable(null);
        setError(availabilityError?.message || 'Could not check availability');
      } finally {
        if (requestIdRef.current === requestId) {
          setChecking(false);
        }
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username, checkUsernameAvailability]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (username.length > 20) {
      setError('Username must be 20 characters or fewer');
      return;
    }

    if (!available) {
      return;
    }

    setLoading(true);
    onContinue(username);
  };

  const handleChange = (e) => {
    const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
    setAvailable(null);
    onUsernameChange(val);
  };

  return (
    <div className="auth-step username-step">
      <button type="button" className="auth-step__back" onClick={onBack} aria-label="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      </button>

      <h2 className="auth-step__title">Choose a username</h2>
      <p className="auth-step__subtitle">This is how others will find you</p>

      <form onSubmit={handleSubmit} className="auth-step__form">
        <div className="auth-input-group">
          <div className="auth-input-wrapper auth-input-wrapper--prefix">
            <span className="auth-input__prefix">@</span>
            <input
              type="text"
              className={`auth-input auth-input--with-prefix ${error ? 'auth-input--error' : ''} ${available ? 'auth-input--success' : ''}`}
              placeholder="username"
              value={username}
              onChange={handleChange}
              maxLength={20}
              autoFocus
              autoComplete="off"
            />
          </div>
          <div className="auth-input__feedback">
            {checking && <p className="auth-input__checking">Checking availability...</p>}
            {error && !checking && <p className="auth-input__error">{error}</p>}
            {available && !checking && !error && <p className="auth-input__success">Username is available!</p>}
          </div>
          <p className="auth-input__hint">{username.length}/20 characters (min 3)</p>
        </div>

        <button
          type="submit"
          className="auth-continue-btn"
          disabled={loading || !available || checking}
        >
          {loading ? 'Setting up...' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

UsernameStep.propTypes = {
  username: PropTypes.string.isRequired,
  onUsernameChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  checkUsernameAvailability: PropTypes.func.isRequired,
};

export default UsernameStep;
